import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api';
import { PageHeader, PageDescription } from '../main/LayoutStyles';
import { StyledButton, AlertBox } from '../main/CommonStyles';
import { AppointmentList, AppointmentCard, AppointmentDetails } from '../specialist/SpecialistPanelStyles';
import { Loader2, AlertTriangle, CheckCircle, XCircle, Users, Clock, ThumbsUp } from 'lucide-react';

const PsychologistAppointments = () => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [processingId, setProcessingId] = useState(null);
    const [cancellingId, setCancellingId] = useState(null);

    const fetchAppointments = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/appointments/psychologist');
            const sorted = response.data.sort((a, b) => {
                const order = {
                    "PENDING_CONFIRMATION": 1,
                    "CONFIRMED": 2,
                    "COMPLETED": 3,
                    "CANCELLED_BY_PATIENT": 4,
                    "CANCELLED_BY_PSYCHOLOGIST": 5
                };
                return (order[a.status] || 99) - (order[b.status] || 99);
            });
            setAppointments(sorted);
        } catch (err) {
            setError('Nie udało się pobrać listy wizyt.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAppointments();
    }, [fetchAppointments]);

    const handleConfirm = async (appointmentId) => {
        setProcessingId(appointmentId);
        try {
            await apiClient.post(`/appointments/${appointmentId}/confirm`);
            fetchAppointments();
        } catch (err) {
            alert('Nie udało się potwierdzić wizyty.');
        } finally {
            setProcessingId(null);
        }
    };

    const handleCancel = async (appointmentId, deleteSlot) => {
        setProcessingId(appointmentId);
        try {
            await apiClient.post(`/appointments/${appointmentId}/cancel?deleteSlot=${deleteSlot}`);
            fetchAppointments();
        } catch (err) {
            alert('Nie udało się odwołać wizyty.');
        } finally {
            setProcessingId(null);
            setCancellingId(null);
        }
    }

    const renderAppointmentsByStatus = (statuses, title, icon) => {
        const filtered = appointments.filter(app => statuses.includes(app.status));
        if (filtered.length === 0) return null;

        return (
            <div style={{marginBottom: '2rem'}}>
                <h3 style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.2rem', color: '#075985'}}>
                    {icon} {title} ({filtered.length})
                </h3>
                <AppointmentList>
                    {filtered.map(app => (
                        <AppointmentCard key={app.id}>
                            <AppointmentDetails>
                                <p><strong>Pacjent:</strong> {app.patientUsername}</p>
                                <p><strong>Data:</strong> {new Date(app.startTime).toLocaleString('pl-PL', { dateStyle: 'long', timeStyle: 'short' })}</p>
                                {app.notes && <p><strong>Notatki:</strong> <em>{app.notes}</em></p>}
                            </AppointmentDetails>
                            <div className="actions-container">
                                {app.status === 'PENDING_CONFIRMATION' && (
                                    <StyledButton $variant="secondary" onClick={() => handleConfirm(app.id)} disabled={processingId === app.id}>
                                        {processingId === app.id ? <Loader2 className="icon-spin"/> : <CheckCircle size={16}/>}
                                        Potwierdź
                                    </StyledButton>
                                )}
                                {app.status === 'CONFIRMED' && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: '#0d9488', fontWeight: '600', padding: '0.5rem' }}><ThumbsUp size={16}/> Potwierdzona</span>
                                )}
                                {(app.status === 'PENDING_CONFIRMATION' || app.status === 'CONFIRMED') && (
                                    <StyledButton $variant="outline" style={{borderColor: '#ef4444', color: '#ef4444'}} onClick={() => setCancellingId(app.id)} disabled={processingId === app.id}>
                                        <XCircle size={16}/> Odwołaj
                                    </StyledButton>
                                )}
                            </div>
                            {cancellingId === app.id && (
                                <div className="cancel-confirm-container">
                                    <p>Jak chcesz odwołać tę wizytę?</p>
                                    <div className="buttons">
                                        <StyledButton onClick={() => handleCancel(app.id, false)} disabled={processingId === app.id} size="sm">
                                            {processingId === app.id ? <Loader2 className="icon-spin"/> : null}
                                            Zwolnij termin
                                        </StyledButton>
                                        <StyledButton onClick={() => handleCancel(app.id, true)} disabled={processingId === app.id} $variant="secondary" size="sm" style={{backgroundColor: '#f87171'}}>
                                            {processingId === app.id ? <Loader2 className="icon-spin"/> : null}
                                            Usuń termin na stałe
                                        </StyledButton>
                                        <StyledButton onClick={() => setCancellingId(null)} $variant="outline" size="sm">Anuluj</StyledButton>
                                    </div>
                                </div>
                            )}
                        </AppointmentCard>
                    ))}
                </AppointmentList>
            </div>
        );
    }

    if (loading) return <div><Loader2 className="icon-spin" /> Ładowanie wizyt...</div>;
    if (error) return <AlertBox type="error"><AlertTriangle /> {error}</AlertBox>;

    return (
        <div>
            <PageHeader>Zarządzaj Wizytami</PageHeader>
            <PageDescription>Przeglądaj i zarządzaj wizytami. Potwierdzaj prośby od pacjentów lub odwołuj spotkania w razie potrzeby.</PageDescription>
            {appointments.length === 0 && !loading && <p>Brak zaplanowanych lub przeszłych wizyt.</p>}
            {renderAppointmentsByStatus(['PENDING_CONFIRMATION'], 'Oczekujące na potwierdzenie', <Clock size={20}/>)}
            {renderAppointmentsByStatus(['CONFIRMED'], 'Potwierdzone nadchodzące wizyty', <Users size={20}/>)}
        </div>
    );
};

export default PsychologistAppointments;